import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import {
  loadUploadedPhotosAsync,
  saveUploadedPhotoAsync,
  deleteUploadedPhotoAsync,
  saveAllUploadedPhotosAsync,
} from "@/utils/galleryStore";
import { uploadImageToCloudDetailed, testCloudUpload, type UploadResult } from "@/utils/cloudImageUpload";
import { builtinPhotos, type GalleryPhoto, defaultHero, type HeroData } from "@/data/siteContent";
import {
  stagingStoreSet,
  stagingStoreNotify,
  getCurrentValueForKey,
} from "@/hooks/useAdminStorage";

const DELETED_BUILTINS_KEY = "ma_gallery_deleted_builtins";
const HERO_DATA_KEY = "ma_hero";

// ─── Built-in photos ──────────────────────────────────────────────────────────
export const BUILTIN_PHOTOS: GalleryPhoto[] = builtinPhotos;

// ─── Helper: get current hero bgImage from staging or localStorage ────────────
function getCurrentHeroBgImage(): string {
  const heroData = getCurrentValueForKey<HeroData>(HERO_DATA_KEY, defaultHero);
  return heroData.bgImage || defaultHero.bgImage;
}

// ─── Helper: set hero bgImage through staging system ─────────────────────────
function setHeroBgImageStaged(url: string): void {
  const heroData = getCurrentValueForKey<HeroData>(HERO_DATA_KEY, defaultHero);
  const updated = { ...heroData, bgImage: url };
  stagingStoreSet(HERO_DATA_KEY, updated);
  stagingStoreNotify(HERO_DATA_KEY);
}

// ─── GalleryPage ─────────────────────────────────────────────────────────────

type FilterTab = "all" | "hero" | "uploaded";

export default function GalleryPage() {
  const { isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [uploadedPhotos, setUploadedPhotos] = useState<GalleryPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [filter, setFilter] = useState<FilterTab>("all");

  // deletedBuiltins goes through staging so it syncs to Supabase
  const [deletedBuiltinIds, setDeletedBuiltinIds] = useState<Set<string>>(() => {
    try {
      // Read from staging first, then localStorage
      const staged = getCurrentValueForKey<string[]>(DELETED_BUILTINS_KEY, []);
      if (staged.length > 0) return new Set(staged);
      const raw = localStorage.getItem(DELETED_BUILTINS_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
    return new Set();
  });

  // Current hero bgImage — read from staging/localStorage
  const [heroResolved, setHeroResolved] = useState<string>(() => getCurrentHeroBgImage());

  const [customUrl, setCustomUrl] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [cloudStatus, setCloudStatus] = useState<{ tested: boolean; ok?: boolean; url?: string; error?: string; details?: UploadResult }>({ tested: false });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadErrorDetails, setUploadErrorDetails] = useState<UploadResult | null>(null);

  // Load uploaded photos from IndexedDB on mount
  useEffect(() => {
    loadUploadedPhotosAsync().then((photos) => {
      setUploadedPhotos(photos);
      setLoadingPhotos(false);
    });
  }, []);

  const bg = isDark ? "bg-gray-950" : "bg-gray-50";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const inputCls = isDark
    ? "border border-gray-600 bg-gray-700 text-white placeholder-gray-500 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "border border-gray-200 bg-white text-gray-800 placeholder-gray-400 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200";

  if (!isAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bg}`}>
        <p className={`text-sm ${subColor}`}>Admin access required.</p>
      </div>
    );
  }

  const allBuiltin = BUILTIN_PHOTOS.filter((p) => !deletedBuiltinIds.has(p.id));
  const allPhotos: GalleryPhoto[] = [...allBuiltin, ...uploadedPhotos];

  const filtered = filter === "all"
    ? allPhotos
    : filter === "hero"
    ? allPhotos.filter((p) => p.isHero)
    : uploadedPhotos;

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDeleteBuiltin = (id: string) => {
    const next = new Set([...deletedBuiltinIds, id]);
    setDeletedBuiltinIds(next);
    const arr = [...next];
    // Stage through sync system so it saves to Supabase
    stagingStoreSet(DELETED_BUILTINS_KEY, arr);
    stagingStoreNotify(DELETED_BUILTINS_KEY);
  };

  const handleDeleteUploaded = async (id: string) => {
    await deleteUploadedPhotoAsync(id);
    setUploadedPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  // ── Upload ──────────────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setPendingFile(file);
    setUploadPreview(previewUrl);
    setShowUploadModal(true);
    e.target.value = "";
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setUploadError(null);
    setUploadErrorDetails(null);
    try {
      const result = await uploadImageToCloudDetailed(pendingFile);
      if (!result.success) {
        setUploadError(result.error || "Upload failed");
        setUploadErrorDetails(result);
        return;
      }
      const newPhoto: GalleryPhoto = {
        id: `upload-${Date.now()}`,
        url: result.url!,
        thumb: result.url!,
        label: pendingFile.name.replace(/\.[^.]+$/, "") || "Uploaded Photo",
        isHero: true,
        isUploaded: true,
      };
      await saveUploadedPhotoAsync(newPhoto);
      setUploadedPhotos((prev) => [...prev, newPhoto]);
      setShowUploadModal(false);
      setPendingFile(null);
      if (uploadPreview) URL.revokeObjectURL(uploadPreview);
      setUploadPreview(null);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
      console.error("Gallery upload error:", msg);
    } finally {
      setUploading(false);
    }
  };

  // ── Add URL ─────────────────────────────────────────────────────────────────
  const handleAddUrl = async () => {
    if (!customUrl.trim()) return;
    const newPhoto: GalleryPhoto = {
      id: `url-${Date.now()}`,
      url: customUrl.trim(),
      thumb: customUrl.trim(),
      label: "Custom Photo",
      isHero: false,
      isUploaded: true,
    };
    await saveUploadedPhotoAsync(newPhoto);
    setUploadedPhotos((prev) => [...prev, newPhoto]);
    setCustomUrl("");
  };

  // ── Set Hero — updates ma_hero.bgImage through staging system ───────────────
  const setAsHero = (photo: GalleryPhoto) => {
    setHeroBgImageStaged(photo.url);
    setHeroResolved(photo.url);
  };

  const setAsHeroFromUrl = (url: string) => {
    setHeroBgImageStaged(url);
    setHeroResolved(url);
  };

  // ── Edit label ──────────────────────────────────────────────────────────────
  const saveLabel = async (id: string) => {
    const next = uploadedPhotos.map((p) => p.id === id ? { ...p, label: editLabel } : p);
    setUploadedPhotos(next);
    await saveAllUploadedPhotosAsync(next);
    setEditingId(null);
  };

  const toggleHeroFlag = async (id: string) => {
    const next = uploadedPhotos.map((p) => p.id === id ? { ...p, isHero: !p.isHero } : p);
    setUploadedPhotos(next);
    await saveAllUploadedPhotosAsync(next);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${bg}`}>
      {/* Top bar */}
      <div className={`sticky top-0 z-40 border-b ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-500">
              <i className="ri-gallery-line text-white text-sm"></i>
            </div>
            <div>
              <span className={`font-bold text-base ${headingColor}`}>Image Gallery</span>
              <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? "bg-pink-900/40 text-pink-400" : "bg-pink-50 text-pink-600"}`}>
                {allPhotos.length} photos
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {uploadSuccess && (
              <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1.5 rounded-full flex items-center gap-1">
                <i className="ri-check-line"></i> Photo saved!
              </span>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full"
            >
              <i className="ri-upload-2-line"></i>
              Upload Photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Important notice */}
        <div className={`rounded-2xl border p-4 ${isDark ? "bg-amber-900/20 border-amber-700" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-amber-500 text-lg mt-0.5 flex-shrink-0"></i>
            <div>
              <p className={`text-sm font-semibold ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                Setting a hero image here stages it for saving — click Save in the admin bar to sync it to all browsers.
              </p>
              <p className={`text-xs mt-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                Hero image changes are part of your site content and will sync to Supabase when you hit Save.
              </p>
            </div>
          </div>
        </div>

        {/* Cloud Status Test */}
        <div className={`rounded-2xl border p-5 ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-3 ${headingColor}`}>
            <i className="ri-cloud-line text-pink-400 mr-1.5"></i>
            Cloud Upload Status
          </h3>
          <div className="flex items-center gap-3">
            {!cloudStatus.tested ? (
              <button
                onClick={async () => {
                  setCloudStatus({ tested: true });
                  const result = await testCloudUpload();
                  setCloudStatus({ tested: true, ...result });
                }}
                className="bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-test-tube-line mr-1"></i>Test Cloud Upload
              </button>
            ) : cloudStatus.ok ? (
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                <span className="text-xs font-semibold text-green-600">Cloud upload is working!</span>
                <span className="text-[10px] text-gray-400 truncate max-w-[200px]">{cloudStatus.url}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                  <span className="text-xs font-semibold text-red-600">Cloud upload failed: {cloudStatus.error}</span>
                  <button
                    onClick={async () => {
                      setCloudStatus({ tested: true });
                      const result = await testCloudUpload();
                      setCloudStatus({ tested: true, ...result });
                    }}
                    className="text-xs text-pink-500 hover:text-pink-700 font-semibold cursor-pointer ml-2"
                  >
                    Retry
                  </button>
                </div>
                {cloudStatus.details && (
                  <div className={`rounded-lg border p-3 text-[10px] font-mono space-y-1 ${isDark ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-500"}`}>
                    <div><span className="font-semibold">Method:</span> {cloudStatus.details.method || "unknown"}</div>
                    <div><span className="font-semibold">HTTP Status:</span> {cloudStatus.details.httpStatus || "N/A"}</div>
                    <div><span className="font-semibold">Step:</span> {cloudStatus.details.step || "N/A"}</div>
                    <div><span className="font-semibold">Response:</span></div>
                    <pre className="whitespace-pre-wrap break-all max-h-32 overflow-y-auto">{cloudStatus.details.responseBody || "No response body"}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className={`text-xs mt-2 ${subColor}`}>
            Click "Test Cloud Upload" to verify that images are being stored on the internet (not just your device).
            If this fails, photos won't appear on other devices.
          </p>
        </div>

        {/* Hero section */}
        <div className={`rounded-2xl border p-5 ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-1 ${headingColor}`}>
            <i className="ri-image-2-line text-pink-400 mr-1.5"></i>
            Hero Background
          </h3>
          <p className={`text-xs mb-4 ${subColor}`}>
            The full-width background image on the homepage hero. Recommended: 1920×1080 (16:9). Click any photo below to apply it, or paste a custom URL. <strong>Remember to Save after changing.</strong>
          </p>
          {heroResolved && (
            <div className="mb-4">
              <p className={`text-xs font-semibold mb-2 ${subColor}`}>Current hero background (staged/saved):</p>
              <div className="relative w-full max-w-sm rounded-xl overflow-hidden border-2 border-pink-400">
                <div className="aspect-[16/9]">
                  <img src={heroResolved} alt="Current hero" className="w-full h-full object-cover" />
                </div>
                <div className="absolute top-2 right-2 bg-pink-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Active</div>
              </div>
            </div>
          )}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="Paste a custom image URL to use as hero..."
              className={`flex-1 ${inputCls}`}
            />
            <button
              onClick={() => { if (customUrl.trim()) { setAsHeroFromUrl(customUrl.trim()); setCustomUrl(""); } }}
              disabled={!customUrl.trim()}
              className="bg-pink-500 hover:bg-pink-600 disabled:opacity-40 text-white text-xs font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              Set as Hero
            </button>
          </div>
          <p className={`text-xs font-semibold mb-2 ${subColor}`}>Or pick from gallery ({allPhotos.filter((p) => p.isHero).length} hero-tagged photos):</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {allPhotos.filter((p) => p.isHero).map((photo) => {
              const isActive = heroResolved === photo.url;
              return (
                <button
                  key={photo.id}
                  onClick={() => setAsHero(photo)}
                  className={`relative group rounded-xl overflow-hidden border-2 cursor-pointer transition-all text-left ${
                    isActive ? "border-pink-500" : isDark ? "border-gray-600 hover:border-pink-400" : "border-gray-200 hover:border-pink-300"
                  }`}
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={photo.thumb} alt={photo.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className={`px-2 py-1.5 ${isDark ? "bg-gray-800" : "bg-white"}`}>
                    <div className={`text-[10px] font-bold truncate ${headingColor}`}>{photo.label}</div>
                  </div>
                  {isActive && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center bg-pink-500 rounded-full">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-pink-500/0 group-hover:bg-pink-500/10 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-pink-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">Set as Hero</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* All photos */}
        <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
          <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? "border-gray-700" : "border-gray-100"}`}>
            <div>
              <h3 className={`font-bold text-sm ${headingColor}`}>
                <i className="ri-gallery-line text-pink-400 mr-1.5"></i>
                All Photos
              </h3>
              <p className={`text-xs mt-0.5 ${subColor}`}>
                Hover any photo to delete it. Click the hero icon to mark it for the hero section.
              </p>
            </div>
            <div className="flex gap-1.5">
              {(["all", "hero", "uploaded"] as FilterTab[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    filter === f
                      ? "bg-pink-500 text-white"
                      : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {f === "all" ? `All (${allPhotos.length})` : f === "hero" ? `Hero (${allPhotos.filter((p) => p.isHero).length})` : `Uploaded (${uploadedPhotos.length})`}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5">
            {loadingPhotos ? (
              <div className="text-center py-12">
                <i className="ri-loader-4-line animate-spin text-2xl text-pink-400"></i>
                <p className={`text-sm mt-2 ${subColor}`}>Loading photos...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                  <i className="ri-image-line text-gray-400 text-xl"></i>
                </div>
                <p className={`text-sm font-semibold mb-1 ${headingColor}`}>No photos here yet</p>
                <p className={`text-xs ${subColor}`}>
                  {filter === "uploaded" ? "Upload a photo using the button above." : "No photos match this filter."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filtered.map((photo) => {
                  const isBuiltin = !photo.isUploaded;
                  const isActive = heroResolved === photo.url;
                  return (
                    <div
                      key={photo.id}
                      className={`relative group rounded-2xl overflow-hidden border-2 transition-all ${
                        isActive ? "border-pink-500" : isDark ? "border-gray-700 hover:border-gray-500" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={photo.thumb}
                          alt={photo.label}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className={`px-3 py-2 ${isDark ? "bg-gray-800" : "bg-white"}`}>
                        {editingId === photo.id ? (
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveLabel(photo.id); if (e.key === "Escape") setEditingId(null); }}
                              autoFocus
                              className={`flex-1 text-xs border rounded px-1.5 py-0.5 focus:outline-none ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                            />
                            <button onClick={() => saveLabel(photo.id)} className="text-green-500 text-xs cursor-pointer">
                              <i className="ri-check-line"></i>
                            </button>
                          </div>
                        ) : (
                          <div className={`text-xs font-bold truncate ${headingColor}`}>{photo.label}</div>
                        )}
                        <div className="flex items-center gap-1.5 mt-1">
                          {photo.isHero && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${isDark ? "bg-pink-900/40 text-pink-400" : "bg-pink-50 text-pink-500"}`}>
                              Hero
                            </span>
                          )}
                          {photo.isUploaded && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"}`}>
                              Uploaded
                            </span>
                          )}
                          {isActive && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${isDark ? "bg-amber-900/40 text-amber-400" : "bg-amber-50 text-amber-600"}`}>
                              Active Hero
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
                        <button
                          onClick={() => setAsHero(photo)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 flex items-center justify-center bg-pink-500 hover:bg-pink-600 text-white rounded-full cursor-pointer"
                          title="Set as hero background"
                        >
                          <i className="ri-image-2-line text-sm"></i>
                        </button>
                        {!isBuiltin && (
                          <button
                            onClick={() => { setEditingId(photo.id); setEditLabel(photo.label); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-white rounded-full cursor-pointer"
                            title="Edit label"
                          >
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                        )}
                        {!isBuiltin && (
                          <button
                            onClick={() => toggleHeroFlag(photo.id)}
                            className={`opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${
                              photo.isHero ? "bg-green-500 hover:bg-green-600" : "bg-gray-500 hover:bg-gray-600"
                            } text-white`}
                            title={photo.isHero ? "Remove hero tag" : "Mark as hero"}
                          >
                            <i className="ri-flag-line text-sm"></i>
                          </button>
                        )}
                        <button
                          onClick={() => isBuiltin ? handleDeleteBuiltin(photo.id) : handleDeleteUploaded(photo.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer"
                          title="Delete photo"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={() => fileRef.current?.click()}
                  className={`aspect-[4/3] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${
                    isDark ? "border-gray-600 text-gray-500 hover:border-pink-400 hover:text-pink-400" : "border-gray-200 text-gray-400 hover:border-pink-300 hover:text-pink-500"
                  }`}
                >
                  <i className="ri-add-line text-2xl"></i>
                  <span className="text-xs font-semibold">Upload</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* How to use */}
        <div className={`rounded-2xl border p-5 ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-3 ${headingColor}`}>
            <i className="ri-information-line text-pink-400 mr-1.5"></i>
            How to Use Gallery Photos on the Site
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: "ri-image-2-line", title: "Hero Background", desc: "Click the pink camera icon on any photo above to stage it as the homepage hero. Then hit Save in the admin bar to sync it everywhere." },
              { icon: "ri-cursor-line", title: "Any Section Image", desc: "Go to the homepage in admin mode, hover over any image, and click the pink Gallery button to pick from here." },
              { icon: "ri-upload-2-line", title: "Upload Your Own", desc: "Click 'Upload Photo' to add your own images. They're stored permanently in the cloud and visible on all devices." },
            ].map((item) => (
              <div key={item.title} className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-pink-100 mb-2">
                  <i className={`${item.icon} text-pink-500 text-sm`}></i>
                </div>
                <div className={`text-xs font-bold mb-1 ${headingColor}`}>{item.title}</div>
                <div className={`text-xs leading-relaxed ${subColor}`}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upload modal */}
      {showUploadModal && uploadPreview && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4">
          <div className={`w-full max-w-md rounded-3xl border p-6 ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
            <h3 className={`font-bold text-base mb-4 ${headingColor}`}>Add Photo to Gallery</h3>
            <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4">
              <img src={uploadPreview} alt="Preview" className="w-full h-full object-cover" />
            </div>
            <p className={`text-xs mb-5 ${subColor}`}>
              This photo will be uploaded to the cloud and visible on all devices.
            </p>
            {uploadError && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3">
                <div className="flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm mt-0.5"></i>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-red-700">Upload failed</p>
                    <p className="text-[10px] text-red-600 mt-0.5 break-all">{uploadError}</p>
                    {uploadErrorDetails && (
                      <div className="mt-2 pt-2 border-t border-red-100">
                        <p className="text-[10px] text-red-500 font-semibold">Method: {uploadErrorDetails.method || "unknown"}</p>
                        {uploadErrorDetails.step && (
                          <p className="text-[10px] text-red-500">Step: {uploadErrorDetails.step}</p>
                        )}
                        {uploadErrorDetails.httpStatus && (
                          <p className="text-[10px] text-red-500">HTTP: {uploadErrorDetails.httpStatus}</p>
                        )}
                        {uploadErrorDetails.responseBody && (
                          <pre className="text-[9px] text-red-400 mt-1 whitespace-pre-wrap break-all max-h-24 overflow-y-auto bg-red-100/50 rounded p-1">{uploadErrorDetails.responseBody}</pre>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={confirmUpload}
                disabled={uploading}
                className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Uploading to cloud...</>
                ) : (
                  <><i className="ri-upload-cloud-2-line mr-1"></i>Upload to Cloud &amp; Add</>
                )}
              </button>
              <button
                onClick={() => { if (!uploading) { setShowUploadModal(false); setPendingFile(null); setUploadError(null); if (uploadPreview) URL.revokeObjectURL(uploadPreview); setUploadPreview(null); } }}
                disabled={uploading}
                className={`flex-1 border font-semibold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300 hover:border-gray-400" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
